import { Component, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import _ from 'lodash';
import { Utils } from '../../../service/utils';
import { CurrentToolPath, Cutter_, Operation_, Strategy, ToolContainer } from '../../../service/formulas/objects';
import { Constants, Toolpath } from '../../../service/constants';
import { ToolPathSelectorDialog } from './toolPathSelector/toolPathSelector';
import { CommonService } from '../../../service/common.service';
import { CacheService } from 'ionic-cache';
import { ImageConstants } from '../../../service/image-constants';
import { OperationProvider } from './operation.service';

@Component({
  selector: 'section-operation',
  templateUrl: 'operation.html',
  providers: [OperationProvider],
  styleUrls: ['operation.scss']
})
export class Operation {
  private subscription: Subscription;
  public operation: FormGroup;
  public operationTypes: object[];
  public toolPaths: object[];
  public toolPathSelections: object[];
  public popupVisible: object;
  public filteredToolPaths: any[] = [];
  public filteredStrategies: Strategy[] = [];
  public current: CurrentToolPath;
  public operationTypeOptions: { [productKey: number]: Operation_[] } = {};
  public currentOperationTypes: Operation_[] = [];
  private currentTool: Cutter_;
  private holder: ToolContainer;
  private enableToolPathSelector: boolean = true;
  private isApp: boolean;
  private selectedOperationType: string;
  public windowsDimensions: object = undefined;
  @Output() operationTypeChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() toolPathChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() strategyChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() setSlotting: EventEmitter<any> = new EventEmitter<any>();
  @Output() setChamferDiameter: EventEmitter<any> = new EventEmitter<any>();
  constructor(
    public utils: Utils,
    public constants: Constants,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private commonService: CommonService,
    private cache: CacheService,
    private imageConstants: ImageConstants,
    private operationProvider: OperationProvider
  ) {
    this.operation = this.formBuilder.group({
      operationTypeSelect: new FormControl({ value: '', disabled: true }),
      toolPathSelect: new FormControl({ value: '', disabled: true }),
      strategySelect: new FormControl({ value: '', disabled: true })
    });
    this.currentTool = new Cutter_();
    this.operationTypes = this.constants.getOperationTypes();
    this.toolPaths = this.constants.getToolPaths();
    this.toolPathSelections = this.constants.getToolPathSelections();
    this.holder = new ToolContainer();

    this.popupVisible = {
      operationTypeSelect: false,
      toolPathSelect: false
    };

    this.current = new CurrentToolPath(
      'Tool Path Info',
      'To select a Tool Path, use the drop down menu above or, to view graphic representations of the paths, click on the button.',
      this.imageConstants.toolPathDefault,
      this.imageConstants.toolPathDefault,
      1
    );

    this.isApp = this.utils.isApp();
    if (this.isApp) {
      this.cache.getItem('windowsDimensions').then(windowsDimensions => {
        this.windowsDimensions = windowsDimensions;
      });
    }

    this.cache.getItem('operations').then(data => {
      this.operationTypeOptions = data;
    });

    this.cache.getItem('operationsCached').then(isCached => {
      // check if we have cached all the endmill information, if not then fetch all the endmills
      if (!isCached) {
        this.fetchAllOperations();
      }
    });

    this.subscription = this.commonService.notifyObservable$.subscribe(res => {
      if (res.hasOwnProperty('option') && res.option === 'changeToolPath') {
        this.operation.get('toolPathSelect').patchValue(res.value, { emitEvent: true });
      }
      if (res.hasOwnProperty('option') && res.option === 'operation') {
        this.holder = res.value;

        if (this.holder.tool) {
          if (this.operationTypeOptions[this.holder.tool.id]) {
            this.getOpertionTypesForTool();
          } else {
            this.operationProvider.fetchEndmillOperations(this.holder.tool.id).subscribe(operations => {
              this.operationTypeOptions[this.holder.tool.id] = operations;
              this.cache.saveItem('operations', this.operationTypeOptions);
              this.getOpertionTypesForTool();
            });
          }
        } else {
          this.operation.get('operationTypeSelect').patchValue('');
          this.setCurrentToolPath();
          this.disableForm();
        }

        // paths should only be reset
        // 1. if tools is changed from chamfer to regular tool, to chamfer then back
        // 2. initial load of tools
        // 3. if parameters suggested to change
        // 4. master reset

        // so if Machine Holder changes, it should NOT change the path

        if (this.holder.tool) {
          /*
            3d Surfacing IF end = Ball-nosed + "the rest"
            Chamfer IF end = % chamfer, ONLY
            others get "the rest", no 3d Surfacing
          */

          let toolEnd = this.holder.tool.end.toLowerCase();
          this.enableToolPathSelector = true;
          if (toolEnd.indexOf('chamfer') > -1) {
            // ONLY enable chamfer and disable all others
            _.each(this.toolPathSelections, path => {
              let pathName = path.getName().toLowerCase();
              if (pathName.indexOf('chamfering') > -1) {
                // enable chamfering
                path.setApplicable(true);
              } else {
                // disable everything else
                path.setApplicable(false);
              }
              this.enableToolPathSelector = false;
            });
          } else if (toolEnd.indexOf('ball-nosed') > -1) {
            // enable 3d surfacing and the rest
            _.each(this.toolPathSelections, path => {
              let pathName = path.getName().toLowerCase();
              if (pathName.indexOf('3d surfacing') > -1) {
                // enabling 3d surfacing
                path.setApplicable(true);
              } else if (pathName.indexOf('chamfering') > -1) {
                // disabling chamfering
                path.setApplicable(false);
              } else {
                // enabling the rest
                path.setApplicable(true);
              }
            });
          } else {
            // enable the rest but not 3d surfacing and no chamfering

            // let check if there is a path already
            _.each(this.toolPathSelections, path => {
              let pathName = path.getName().toLowerCase();
              if (pathName.indexOf('3d surfacing') > -1) {
                // disable 3d surfacing
                path.setApplicable(false);
              } else if (pathName.indexOf('chamfering') > -1) {
                // disable chamfering
                path.setApplicable(false);
              } else {
                // enable everythingelse
                path.setApplicable(true);
              }
            });
          }

          if (
            this.current.name === 'Tool Path Info' ||
            this.current.name === 'Chamfering' ||
            toolEnd.indexOf('chamfer') > -1 ||
            this.currentTool !== this.holder.tool
          ) {
            this.currentTool = this.holder.tool;
            let preselectedPath = this.operation.get('toolPathSelect').value;

            if (typeof preselectedPath === 'object') {
              if (preselectedPath) {
                preselectedPath = preselectedPath.value;
              }
            }
            if (toolEnd.indexOf('chamfer') > -1) {
              this.operation.get('toolPathSelect').patchValue('Chamfering', { emitEvent: true });
            } else {
              // First need to check if preselected path is still set to Chamfering
              // This happens when user switches from a Chamfering tool to a non-chamfering tool
              if (preselectedPath === 'Chamfering') {
                this.operation.get('operationTypeSelect').patchValue(this.selectedOperationType, { emitEvent: false });
                this.operation.get('toolPathSelect').reset({ value: '' }, { emitEvent: true });
                this.holder.currenttoolpath = undefined;
                this.setCurrentToolPath();
                setTimeout(() => {
                  this.commonService.notifyOther({
                    option: 'switchedFromChamferingTool',
                    value: this.holder
                  });
                });
              }
            }
            if (this.filteredToolPaths) {
              for (const toolPathOption of this.filteredToolPaths) {
                if (toolPathOption.operationName === '3D Surfacing') {
                  this.operation.get('toolPathSelect').patchValue('3D Surfacing', { emitEvent: true });
                  // Set 3D surfacing to selected tool path
                  const operationToolPath = toolPathOption;
                  this.current.name = operationToolPath.operationToolPathTitle;
                  this.current.description = operationToolPath.operationToolPathDescription;
                  this.current.labledImage = operationToolPath.operationImage
                    ? operationToolPath.operationImage
                    : this.imageConstants.unavailableOperationThumbnail;
                  this.current.unLabledImage = operationToolPath.operationImage
                    ? operationToolPath.operationImage
                    : this.imageConstants.unavailableOperationThumbnail;
                  this.current.operationImageAssetForParameterSection =
                    operationToolPath.operationImageAssetForParameterSection;
                  this.current.mapMode = operationToolPath.mapMode;
                  this.current.toolPathId = operationToolPath.toolPathId;
                  this.filteredStrategies = operationToolPath.strategies;
                  this.commonService.notifyOther({
                    option: 'mapModeChanged',
                    value: operationToolPath
                  });
                  this.toolPathChange.emit(operationToolPath);
                  break;
                }
              }
            }
          } else if (!!this.current && !!this.current.name) {
            if (res.updateToolPath) {
              this.setCurrentToolPath(this.current);
            }
          }
        }
      } else if ((res.hasOwnProperty('option') && res.option === 'reset') || res.option === 'resetTool') {
        this.operation.setValue(
          {
            operationTypeSelect: { value: '', disabled: true },
            toolPathSelect: { value: '', disabled: true },
            strategySelect: { value: '', disabled: true }
          },
          { emitEvent: false }
        );
        this.currentOperationTypes = [];
        this.filteredToolPaths = [];
        this.filteredStrategies = [];
        this.disableForm();
        this.setCurrentToolPath();
      }
    });
  }

  private fetchAllOperations = () => {
    this.operationProvider.getOperations().subscribe(
      operations => {
        this.cache.saveItem('operations', operations);
        this.operationTypeOptions = operations;
        this.cache.saveItem('operationsCached', true);
      },
      error => {
        console.log(error);
        this.cache.saveItem('operationsCached', false);
      }
    );
  };

  ngAfterViewInit() {
    this.subscription = this.commonService.notifyObservable$.subscribe(res => {
      if (res.option === 'logout') {
        this.subscription.unsubscribe();
      }
    });
  }

  getOpertionTypesForTool = () => {
    const previousOperationType = this.operation.get('operationTypeSelect').value;
    this.currentOperationTypes = this.operationTypeOptions[this.holder.tool.id];

    if (this.currentOperationTypes.length > 0) {
      this.enableForm();
      if (this.currentOperationTypes.length === 1) {
        this.selectedOperationType = this.currentOperationTypes[0].operation;
        this.operation.get('operationTypeSelect').patchValue(this.selectedOperationType);
        this.filteredToolPaths = this.currentOperationTypes[0].toolPaths;
        this.operationTypeChange.emit(this.selectedOperationType);

        const selectedToolpathName = this.operation.get('toolPathSelect').value;
        const selectedStrategyName = this.operation.get('strategySelect').value;
        if (this.currentOperationTypes[0].toolPaths.length === 1) {
          const operationToolPath = this.currentOperationTypes[0].toolPaths[0];
          this.current.name = operationToolPath.operationToolPathTitle;
          this.current.description = operationToolPath.operationToolPathDescription;
          this.current.labledImage = operationToolPath.operationImage
            ? operationToolPath.operationImage
            : this.imageConstants.unavailableOperationThumbnail;
          this.current.unLabledImage = operationToolPath.operationImage
            ? operationToolPath.operationImage
            : this.imageConstants.unavailableOperationThumbnail;
          this.current.operationImageAssetForParameterSection =
            operationToolPath.operationImageAssetForParameterSection;
          this.current.mapMode = operationToolPath.mapMode;
          this.current.toolPathId = operationToolPath.toolPathId;
          this.filteredStrategies = operationToolPath.strategies;
          this.commonService.notifyOther({
            option: 'mapModeChanged',
            value: operationToolPath
          });
          this.toolPathChange.emit(operationToolPath);
        } else if (!!selectedToolpathName) {
          // Clear out tool path because it doesn't exist in the new tool path options
          const newToolPath = this.filteredToolPaths.find(ftp => ftp.operationName === selectedToolpathName);

          if (!newToolPath) {
            // This will handle changing map mode and clearing out the strategy
            this.setCurrentToolPath();
          } else if (!!selectedStrategyName) {
            const newStrategy =
              newToolPath.strategies && newToolPath.strategies.find(str => str.operationName === selectedStrategyName);
            if (!!newStrategy) {
              this.holder.mapMode = newStrategy.mapMode;
              this.current.operationImageAssetForParameterSection = newStrategy.operationImageAssetForParameterSection;
              this.current.mapMode = newStrategy.mapMode;
              this.current.operationKey = newStrategy.operationKey;
              this.current.toolPathId = newStrategy.strategyId;
              this.commonService.notifyOther({
                option: 'mapModeChanged',
                value: this.current
              });
            } else {
              // This will handle changing map mode and clearing out the strategy
              this.setCurrentToolPath();
            }
          } else {
            this.current.name = newToolPath.operationToolPathTitle;
            this.current.description = newToolPath.operationToolPathDescription;
            this.current.labledImage = newToolPath.operationImage
              ? newToolPath.operationImage
              : this.imageConstants.unavailableOperationThumbnail;
            this.current.unLabledImage = newToolPath.operationImage
              ? newToolPath.operationImage
              : this.imageConstants.unavailableOperationThumbnail;
            this.current.operationImageAssetForParameterSection = newToolPath.operationImageAssetForParameterSection;
            this.current.mapMode = newToolPath.mapMode;
            this.current.toolPathId = newToolPath.toolPathId;
            this.filteredStrategies = newToolPath.strategies;
            this.commonService.notifyOther({
              option: 'mapModeChanged',
              value: newToolPath
            });
          }
        }
      } else {
        const newOperation = this.currentOperationTypes.find(
          operation => operation.operation === previousOperationType
        );
        if (!newOperation) {
          // Clear out the selected tool if user is going to multiple operation types and the previous type isn't an option
          this.operation.get('operationTypeSelect').patchValue('');
          this.setCurrentToolPath();
        } else {
          this.filteredToolPaths = newOperation.toolPaths;
        }
      }
    } else {
      this.operation.get('operationTypeSelect').patchValue('');
      this.setCurrentToolPath();
      this.disableForm();
    }
  };

  onEnter = (control: string) => {
    if (this.operation.get(control).value !== '') {
      this.popupVisible[control] = false;
    } else {
      this.popupVisible[control] = true;
    }
  };

  onLeave = (control: string) => {
    this.popupVisible[control] = false;
  };

  onKeyUp = (control: string) => {
    if (this.operation.get(control).value !== '') {
      this.popupVisible[control] = false;
    } else {
      this.popupVisible[control] = true;
    }
  };

  select = (control: string) => {
    this.popupVisible[control] = false;
  };

  setType = (type: any, event: MatOptionSelectionChange) => {
    if (event.isUserInput) {
      this.selectedOperationType = type.operation;
      this.operation.get('operationTypeSelect').patchValue(this.selectedOperationType);
      this.filteredToolPaths = type.toolPaths;
      this.operationTypeChange.emit(this.selectedOperationType);
      this.enableForm();
    }
  };

  setCurrentToolPath = (value?: CurrentToolPath, control?: string, event?: MatOptionSelectionChange) => {
    if (!value) {
      // had to force and just copy the same line in the constructor.
      // for whatever reason the defaultToolPath was being replaced
      this.current = new CurrentToolPath(
        'Tool Path Info',
        'To select a Tool Path, use the drop down menu above or, to view graphic representations of the paths, click on the button.',
        this.imageConstants.toolPathDefault,
        this.imageConstants.toolPathDefault,
        1
      );
      // if there is no value set mapMode to default
      this.holder.mapMode = 0;
      this.current.mapMode = 0;
      this.current.toolPathId = null;
      this.commonService.notifyOther({
        option: 'clearToolPath',
        value: this.current
      });
      this.filteredStrategies = [];
      this.operation.get('toolPathSelect').patchValue('', { emitEvent: true });
      this.operation.get('strategySelect').patchValue('', { emitEvent: false });
    } else if (
      ((!!event && event.isUserInput) || control === 'toolPathImageSelector') &&
      this.current.toolPathId !== value.toolPathId
    ) {
      let toolPath = value;

      if (toolPath) {
        this.current.name = toolPath.operationToolPathTitle;
        this.current.description = toolPath.operationToolPathDescription;
        this.current.labledImage = toolPath.operationImage
          ? toolPath.operationImage
          : this.imageConstants.unavailableOperationThumbnail;
        this.current.unLabledImage = toolPath.operationImage
          ? toolPath.operationImage
          : this.imageConstants.unavailableOperationThumbnail;
        this.current.operationImageAssetForParameterSection = toolPath.operationImageAssetForParameterSection;
        this.current.mapMode = toolPath.mapMode;
        this.current.toolPathId = value.toolPathId;
        this.filteredStrategies = toolPath.strategies;
      } else {
        // chose from drop down

        //toolPaths
        let toolPath: Toolpath = _.find(this.toolPathSelections, (toolPath: Toolpath) => {
          return value.operationToolPathTitle.toLowerCase() === toolPath.getName().toLowerCase();
        }).getToolPaths()[0];

        if (toolPath) {
          this.current.name = toolPath.operationToolPathTitle;
          this.current.description = toolPath.operationToolPathDescription;
          this.current.labledImage = toolPath.operationImage
            ? toolPath.operationImage
            : this.imageConstants.unavailableOperationThumbnail;
          this.current.unLabledImage = toolPath.operationImage
            ? toolPath.operationImage
            : this.imageConstants.unavailableOperationThumbnail;
          this.current.operationImageAssetForParameterSection = toolPath.operationImageAssetForParameterSection;
          this.current.mapMode = toolPath.mapMode;
          this.filteredStrategies = toolPath.strategies;
          this.current.toolPathId = value.toolPathId;
        }
      }

      this.commonService.notifyOther({
        option: 'mapModeChanged',
        value: this.current
      });

      if (this.isApp) {
        this.utils.apptentiveEngage('Tool Path (Drop Down)');
      }
      this.popupVisible[control] = false;
      this.toolPathChange.emit(value);

      // Check if there is a selected strategy and if it exists on selected tool path
      const selectedStrategyName = this.operation.get('strategySelect').value;
      if (toolPath.strategies && toolPath.strategies.length > 0 && !!selectedStrategyName) {
        this.toolPathChange.emit(value);
        const newStrategy = toolPath.strategies.find(str => str.operationName === selectedStrategyName);
        if (newStrategy) {
          this.holder.mapMode = newStrategy.mapMode;
          this.current.operationImageAssetForParameterSection = newStrategy.operationImageAssetForParameterSection;
          this.current.mapMode = newStrategy.mapMode;
          this.current.operationKey = newStrategy.operationKey;
          this.current.toolPathId = newStrategy.strategyId;
          this.strategyChange.emit(newStrategy);
        } else {
          this.operation.get('strategySelect').patchValue('', { emitEvent: false });
          this.strategyChange.emit('');
        }
      }

      this.commonService.notifyOther({
        option: 'checkToGetParamsAndRecs'
      });
      setTimeout(() => {
        this.commonService.notifyOther({
          option: 'recommendationsGeneratedForLoggingData',
          value: {
            logLocation: 'operation change'
          }
        });
      });
    }
  };

  setStrategy = (selectedStrategy: Strategy, event: MatOptionSelectionChange) => {
    if (event.isUserInput) {
      this.operation.get('strategySelect').patchValue(selectedStrategy.operationName);
      this.holder.mapMode = selectedStrategy.mapMode;
      this.current.operationImageAssetForParameterSection = selectedStrategy.operationImageAssetForParameterSection;
      this.current.mapMode = selectedStrategy.mapMode;
      this.current.operationKey = selectedStrategy.operationKey;
      this.current.toolPathId = selectedStrategy.strategyId;
      this.commonService.notifyOther({
        option: 'mapModeChanged',
        value: this.current
      });
      this.strategyChange.emit(selectedStrategy);
      this.commonService.notifyOther({
        option: 'checkToGetParamsAndRecs'
      });
      setTimeout(() => {
        this.commonService.notifyOther({
          option: 'recommendationsGeneratedForLoggingData',
          value: {
            logLocation: 'operation change'
          }
        });
      });
    }
  };

  openToolPathSelector = () => {
    if (this.enableToolPathSelector) {
      let dialogRef = this.dialog.open(ToolPathSelectorDialog, {
        panelClass: 'large-full-screen-dialog',
        data: {
          toolPaths: this.filteredToolPaths
        }
      });
      let instance = dialogRef.componentInstance;

      instance.dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (result['operationName']) {
            let selectedToolPath = _.find(this.filteredToolPaths, function(tp) {
              return tp.operationName === result['operationName'];
            });
            this.operation.get('toolPathSelect').patchValue(selectedToolPath.operationName, { emitEvent: true });
            this.setCurrentToolPath(selectedToolPath, 'toolPathImageSelector');
          }
        }
      });
    }
  };

  enableForm = () => {
    this.operation.enable();
  };

  disableForm = () => {
    this.operation.disable();
  };
}
